import useCapabilities from "@/hooks/useCapabilities";
import { PERMISSION, ROLE } from "@/providers/AuthorizationProvider/types";
import { isActivityType, type ActivityTypeKind } from "@/pages/activities/activity-types/_utils";
import { type ActivityType, CreatesEventType, SectionAvailability } from "@/pages/activities/activity-types/types";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import { isSectionScopedForEvents } from "@/pages/events/_nationalSectionBypass";

export type ActivityTypePermissionsInput = Partial<
  Pick<ActivityType, "title" | "sectionAvailability" | "createsEvent">
>;

export type ActivityTypeManagementCapabilities = Pick<
  ReturnType<typeof useCapabilities>,
  "loaded" | "hasRole" | "hasPermission"
>;

const ACTIVITY_TYPE_PERMISSION_MAP: Partial<Record<ActivityTypeKind, PERMISSION>> = {
  recruitment: PERMISSION.manageRecruitmentActivities,
  pat: PERMISSION.managePATActivities,
  "national-meeting": PERMISSION.manageNationalActivities,
  championship: PERMISSION.manageChampionshipActivities,
  "education-self-paced": PERMISSION.manageEducationActivities,
  "instructor-led": PERMISSION.manageEducationActivities,
  "education-self-reported": PERMISSION.manageEducationActivities
};

const ACTIVITY_TYPE_KINDS: ActivityTypeKind[] = [
  "recruitment",
  "pat",
  "national-meeting",
  "championship",
  "education-self-paced",
  "instructor-led",
  "education-self-reported"
];

function getActivityTypePermission(title: string | undefined): PERMISSION | undefined {
  if (!title) return undefined;
  const matchedType = ACTIVITY_TYPE_KINDS.find((k) => isActivityType(title, k));
  return matchedType ? ACTIVITY_TYPE_PERMISSION_MAP[matchedType] : undefined;
}

export function getCanAccessActivityManagement(
  activityData: ActivityTypePermissionsInput,
  capabilities: ActivityTypeManagementCapabilities,
  primarySectionId?: string | null,
  managingSectionId?: string | null
): boolean {
  const { title, sectionAvailability, createsEvent } = activityData;
  const { hasRole, loaded: capabilitiesLoaded, hasPermission } = capabilities;

  // Intentional: same dual-role national bypass as event clamps, so activity-type
  // pages and person PDR filters stay aligned with EventTypesQueries /
  // CreateActivityTypeParticipation (national roles win over section staff).
  const isSectionUser = isSectionScopedForEvents({ loaded: capabilitiesLoaded, hasRole });
  const isSectionOwner = primarySectionId === managingSectionId;
  const hasManagingSectionContext = managingSectionId !== undefined && primarySectionId !== undefined;

  const isMembershipServicesStaff =
    capabilitiesLoaded && (hasRole(ROLE.MEMBERSHIP_SERVICES_STAFF) || hasRole(ROLE.MEMBERSHIP_SERVICES_ADMIN));

  const isSectionAllowedToCreate = isSectionUser && sectionAvailability === SectionAvailability.CAN_CREATE;
  const canCreateAsSectionOwner = isSectionAllowedToCreate && (!hasManagingSectionContext || !!isSectionOwner);

  const activityTypePermission = getActivityTypePermission(title);
  const hasActivityTypePermission = !!activityTypePermission && hasPermission(activityTypePermission);

  const canManageEvents =
    !isSectionUser && hasPermission(PERMISSION.manageEvents) && createsEvent !== CreatesEventType.NEVER;

  return (
    canCreateAsSectionOwner ||
    isMembershipServicesStaff ||
    hasActivityTypePermission ||
    canManageEvents ||
    hasRole(ROLE.GLOBAL_ADMIN)
  );
}

export default function useActivityTypePermissions(
  activityData: ActivityTypePermissionsInput,
  managingSectionId?: string | null
): boolean {
  const capabilities = useCapabilities();
  const { primarySectionId } = usePrimarySection();
  return getCanAccessActivityManagement(activityData, capabilities, primarySectionId, managingSectionId);
}
